import { fetchSaltclickIdentityToken } from '../clients/fetchSaltclickIdentityToken';
import {
  closeSaltclickConversation,
  fetchOpenSaltclickConversationIds,
  fetchSaltclickScrt2AccessToken,
} from '../clients/saltclickScrt2Client';

export interface ICloseOpenSaltclickConversationsArgs {
  /** From `SALTCLICK_CHAT_CONFIG` — the same values the widget is init'd with. */
  scrt2Url: string;
  orgId: string;
  deploymentName: string;
  /**
   * A conversation to leave alone, when one is live.
   *
   * Load-bearing rather than an optimisation. This closes everything Salesforce reports
   * open, and a chat the user has started since is in that list — so without this, a
   * retirement that runs a moment late takes the conversation they are sitting in. The
   * ended one is retired; the live one is none of its business.
   *
   * Omitted by logout, where nothing is live.
   */
  keepConversationId?: string;
}

/**
 * Closes every conversation Salesforce still holds open for this account, and resolves
 * with how many actually closed.
 *
 * Three hops, the first two of them the widget's own: mint an identity JWT, trade it
 * for a scrt2 access token, list what is open. Then `DELETE
 * /iamessage/v1/conversation/{id}` on each, which is the operation no client API on
 * this deployment offers — `clearSession(true)` only ever reaches the `/session`
 * variant, which ends a session and leaves the conversation open and resumable.
 *
 * Called when the user ends a chat, and never on its own schedule. An earlier version
 * of this swept before `bootstrap.init` to clear conversations left by a document that
 * no longer exists, and the cost was too high: most open conversations are perfectly
 * live, so a refresh mid-chat took the conversation away mid-sentence. Resuming what is
 * open is the widget's own behaviour and the right one.
 *
 * Reads back afterwards rather than trusting the deletes: a `204` that left the
 * conversation open looks identical to one that worked, and server state is the whole
 * question. The read-back reuses the access token — it is account-scoped, not
 * conversation-scoped, so it outlives the conversations it just closed.
 *
 * Rejects when a hop fails, so the caller can decide whether to try again. A single
 * conversation refusing to close is not that: it is logged, counted, and the rest still
 * go.
 */
export const closeOpenSaltclickConversations = async ({
  scrt2Url,
  orgId,
  deploymentName,
  keepConversationId,
}: ICloseOpenSaltclickConversationsArgs): Promise<number> => {
  const identityToken = await fetchSaltclickIdentityToken();
  const accessToken = await fetchSaltclickScrt2AccessToken({
    scrt2Url,
    orgId,
    deploymentName,
    identityToken: identityToken.token,
  });

  const openConversationIds = (
    await fetchOpenSaltclickConversationIds({ scrt2Url, accessToken })
  ).filter((conversationId) => conversationId !== keepConversationId);

  if (!openConversationIds.length) {
    // Said out loud because a path that closed nothing is otherwise indistinguishable
    // from one that never ran.
    console.log('[Saltclick] Nothing open to close — the next chat is already a new conversation');

    return 0;
  }

  const closed = await Promise.all(
    openConversationIds.map((conversationId) =>
      closeSaltclickConversation({ scrt2Url, accessToken, conversationId })
        .then(() => conversationId)
        .catch((err) => {
          // A 403 is worth spelling out, because the message Salesforce sends
          // ("Permission Denied") describes neither of the two things that cause it: the
          // conversation closed between the list and the delete, or this token does not
          // own it. The read-back below settles which.
          console.error(
            `[Saltclick] Could not close conversation ${conversationId} — either it ` +
              'closed already or this token does not own it: ',
            err,
          );

          return undefined;
        }),
    ),
  );

  const closedIds = closed.filter((conversationId): conversationId is string => !!conversationId);
  console.log(
    `[Saltclick] Closed ${closedIds.length} of ${openConversationIds.length} open ` +
      `conversation(s) after the chat ended: ${closedIds.join(', ') || 'none'}`,
  );

  try {
    const stillOpen = (await fetchOpenSaltclickConversationIds({ scrt2Url, accessToken })).filter(
      (conversationId) => conversationId !== keepConversationId,
    );
    if (stillOpen.length) {
      console.error(
        `[Saltclick] Salesforce still lists ${stillOpen.length} open conversation(s) ` +
          `after the close, so the next chat would resume one: ${stillOpen.join(', ')}`,
      );
    } else {
      console.log('[Saltclick] Salesforce lists nothing open — the next chat starts fresh');
    }
  } catch (err) {
    // The close is not undone by a failed read-back, and re-running it on this account
    // would be the wrong response to not knowing. Reported as what it is: unverified.
    console.error('[Saltclick] Could not read back what is still open: ', err);
  }

  return closedIds.length;
};
